// @flow
import React, { type Node, useRef, useCallback, useEffect } from 'react';
import forEach from 'ramda/es/forEach';
import toPairs from 'ramda/es/toPairs';


const DEFAULT: 'default' = 'default';
const COLORED: 'colored' = 'colored';
const BW: 'bw' = 'bw';

export const themeTypes = { DEFAULT, COLORED, BW };

const themes = {
  [themeTypes.DEFAULT]: {
    '--mark-default': '#eb4d4d',
    '--mark-1': '#eb4d4d',
    '--mark-2': '#eb4d4d',
    '--mark-3': '#111',
    '--mark-4': '#039be5',
    '--mark-5': '#039be5',
  },
  [themeTypes.COLORED]: {
    '--mark-default': '#b71c1c',
    '--mark-1': '#b71c1c',
    '--mark-2': '#b71c1c',
    '--mark-3': '#f57c00',
    '--mark-4': '#f57c00',
    '--mark-5': '#43a047',
  },
  [themeTypes.BW]: {
    '--mark-default': '#111',
    '--mark-1': '#111',
    '--mark-2': '#111',
    '--mark-3': '#111',
    '--mark-4': '#111',
    '--mark-5': '#111',
  },
};

type Props = {
  children: Node,
  theme?: $Values<typeof themeTypes>
}

const MarksThemeProvider = ({ children, theme = themeTypes.DEFAULT }: Props): Node => {
  const themeProviderElement = useRef();
  const firstRender = useRef(true);

  const setTheme = useCallback((currentTheme: $Values<typeof themeTypes>) => {
    const targetElem = themeProviderElement.current;
    if (targetElem) {
      forEach(([property, value]) => {
        targetElem.style.setProperty(property, value);
      }, toPairs(themes[currentTheme]));
    }
  }, []);

  const setThemeOnMount = useCallback((elem: HTMLElement | null) => {
    if (elem) {
      themeProviderElement.current = elem;
      setTheme(theme);
    }
  }, [setTheme, theme]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setTheme(theme);
  }, [theme, setTheme]);

  return (
    <div id="marks_theme_provider" ref={setThemeOnMount}>
      {children}
    </div>
  );
};

export default MarksThemeProvider;
